import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Collapse,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Tooltip,
  Breadcrumbs,
  Link,
  Popover,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  AccountBalance,
  Dashboard,
  Receipt,
  People,
  Groups,
  Inventory,
  Payment,
  Logout,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Send,
  Settings,
  AccountCircle,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  NavigateNext,
  Home,
  Business,
  Check,
  Paid,
  Payments,
  AdminPanelSettings,
  Storage,
  History,
  Visibility,
  Description,
  CreditCard,
  Summarize,
  RequestQuote,
  ManageSearch,
  LocalShipping,
  ContactPage,
  Folder,
  Category,
  Warehouse,
  MenuBook,
  ListAlt,
  Email,
  Schedule,
  EventNote,
  PointOfSale,
  Assignment,
  AssignmentInd,
  ChatBubbleOutline,
  Analytics,
  Straighten,
  AccessTime,
  FolderSpecial,
  TrendingUp,
  Handshake,
  ReceiptLong,
  CloudUpload,
  Build,
  CheckCircle,
  ViewColumn,
  SupportAgent,
  ConfirmationNumber,
  CalendarMonth,
  InfoOutlined,
  NotificationsActive,
  Search,
  HowToVote,
  FactCheck,
  LibraryBooks,
  Speed,
  CompareArrows,
} from '@mui/icons-material';
import { ViiluStoreIcon } from '@viilutalous/store';
import { useSupport } from '@viilutalous/helpdesk';
import { useAuth } from '../modules/auth';
import { useTranslation } from 'react-i18next';
import SettingsFinderDialog from './SettingsFinderDialog';
import { DarkModeToggle, NotificationBell, api } from '@viilutalous/common';
import LanguageSwitcher from './LanguageSwitcher';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useTenant } from '../contexts/TenantContext';
import MigrationWarningBanner from './MigrationWarningBanner';
import { ViiluLogo, ViiluIcon } from './ViiluLogo';
import { useChat } from '../contexts/ChatContext';
import { useStore } from '@viilutalous/store';
import ChatPanel from './ChatPanel';

const drawerWidthExpanded = 255;
const drawerWidthCollapsed = 64;

export default function Layout() {
  return <LayoutChrome />;
}

function LayoutChrome() {
  const rawNavigate = useNavigate();
  const rawLocation = useLocation();
  const { logout, user } = useAuth();
  const { t, i18n } = useTranslation();
  const { itemTitle, parentTitle } = useBreadcrumb();
  const { tenants, currentTenant, selectTenant } = useTenant();
  // Slug-stripped view of the URL. All existing path matching (breadcrumbs,
  // active-menu detection, etc.) is written against `/app/<page>` paths from
  // before VII-270 — by stripping the `/<slug>` segment up front we don't have
  // to edit each one of the 60+ pathname call sites.
  const slug = currentTenant?.slug;
  const location = useMemo(() => {
    if (!slug || !rawLocation.pathname.startsWith(`/app/${slug}/`)) return rawLocation;
    const stripped = '/app' + rawLocation.pathname.slice(`/app/${slug}`.length);
    return { ...rawLocation, pathname: stripped };
  }, [rawLocation, slug]);
  // Slug-aware navigate: rewrites `/app/<page>` calls to `/app/<currentSlug>/<page>`
  // so sidebar links and other Layout-owned navigations target the active tenant.
  // Non-/app paths (e.g. `/login`) pass through untouched.
  const navigate = (path, options) => {
    const slug = currentTenant?.slug;
    if (slug && typeof path === 'string' && path.startsWith('/app/') && !path.startsWith(`/app/${slug}/`)) {
      return rawNavigate(`/app/${slug}${path.slice('/app'.length)}`, options);
    }
    return rawNavigate(path, options);
  };
  const { isOpen: isChatOpen, toggleChat, setCurrentPage } = useChat();
  const { enabledModules, openStore } = useStore();
  const { openSupport } = useSupport();
  const isModuleEnabled = (key) => enabledModules ? enabledModules.has(key) : false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [invoicingOpen, setInvoicingOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [purchasingOpen, setPurchasingOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [salariesOpen, setSalariesOpen] = useState(false);
  const [timeTrackingOpen, setTimeTrackingOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [openItemsOpen, setOpenItemsOpen] = useState(false);
  const [sendingServiceOpen, setSendingServiceOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState(null);
  const [tenantSearch, setTenantSearch] = useState('');
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverContent, setPopoverContent] = useState([]);
  const [popoverTitle, setPopoverTitle] = useState('');
  const [popoverTimeout, setPopoverTimeout] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const drawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  // Fetch user profile for photo
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        setUserProfile(response.data);
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };
    if (user) {
      fetchProfile();
    }
  }, [user]);

  // Track current page for chat context
  useEffect(() => {
    setCurrentPage(location.pathname);
  }, [location.pathname, setCurrentPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popoverTimeout) {
        clearTimeout(popoverTimeout);
      }
    };
  }, [popoverTimeout]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Module-based menu structure
  const invoicingMenuItems = [
    { text: t('nav.invoices'), path: '/app/invoices', icon: <Description fontSize="small" /> },
    // Contract invoicing is its own store module (BIZ-868): the basic subscription has invoicing without it.
    ...(isModuleEnabled('contract_invoicing')
      ? [{ text: t('nav.contract_invoices'), path: '/app/contract-invoices', icon: <Description fontSize="small" /> }]
      : []),
    { text: t('nav.payments'), path: '/app/payments', icon: <CreditCard fontSize="small" /> },
    { text: t('nav.payment_notifications'), path: '/app/payment-notifications', icon: <NotificationsActive fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/invoice-reports', icon: <ListAlt fontSize="small" /> },
    // Massalaskutus and metered billing share the store module. They live under
    // Invoicing for discoverability, so they need their own gate — otherwise
    // enabling Invoicing alone would surface pages that answer Access Denied.
    ...(isModuleEnabled('metered_billing')
      ? [
          { text: t('nav.bulk_invoicing'), path: '/app/bulk-invoicing', icon: <ReceiptLong fontSize="small" /> },
          { text: t('metered_billing.title'), path: '/app/metered-billing/periods', icon: <Speed fontSize="small" /> },
        ]
      : []),
  ];

  const salesMenuItems = [
    { text: t('nav.sales_orders'), path: '/app/sales-orders', icon: <Assignment fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/sales-reports', icon: <ListAlt fontSize="small" /> },
  ];

  const purchasingMenuItems = [
    { text: t('nav.purchase_invoices'), path: '/app/purchase-invoices', icon: <RequestQuote fontSize="small" /> },
    { text: t('nav.purchase_orders'), path: '/app/purchase-orders', icon: <Assignment fontSize="small" /> },
    { text: t('nav.charges'), path: '/app/charges', icon: <LocalShipping fontSize="small" /> },
    { text: t('nav.pending_approvals'), path: '/app/purchasing/pending-approvals', icon: <HowToVote fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/purchasing-reports', icon: <ListAlt fontSize="small" /> },
  ];

  const expensesMenuItems = [
    { text: t('nav.expenses'), path: '/app/expenses', icon: <ReceiptLong fontSize="small" /> },
  ];

  const crmMenuItems = [
    { text: t('nav.contacts'), path: '/app/contacts', icon: <ContactPage fontSize="small" /> },
    { text: t('nav.contact_groups'), path: '/app/contact-groups', icon: <Folder fontSize="small" /> },
    { text: t('nav.activities'), path: '/app/activities', icon: <EventNote fontSize="small" /> },
    { text: t('nav.leads'), path: '/app/leads', icon: <TrendingUp fontSize="small" /> },
    { text: t('nav.deals'), path: '/app/deals', icon: <Handshake fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/crm-reports', icon: <ListAlt fontSize="small" /> },
  ];

  const inventoryMenuItems = [
    { text: t('nav.products'), path: '/app/products', icon: <Category fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/inventory-reports', icon: <ListAlt fontSize="small" /> },
  ];

  const accountingMenuItems = [
    { text: t('nav.vouchers'), path: '/app/vouchers', icon: <MenuBook fontSize="small" /> },
    { text: t('nav.talousarvio', 'Talousarvio'), path: '/app/talousarvio', icon: <RequestQuote fontSize="small" /> },
    { text: t('nav.ledger_browser', 'Kirjanpidon selailu'), path: '/app/ledger-browser', icon: <ManageSearch fontSize="small" /> },
    { text: t('nav.account_matching', 'Kohdistus tilin sisällä'), path: '/app/account-matching', icon: <CompareArrows fontSize="small" /> },
    { text: t('nav.reports'), path: '/app/accounting-reports', icon: <ListAlt fontSize="small" /> },
  ];

  const bankMenuItems = [
    { text: t('nav.bank.payments'), path: '/app/bank/payments', icon: <CreditCard fontSize="small" /> },
    { text: t('nav.bank.overview'), path: '/app/bank/payments/overview', icon: <Summarize fontSize="small" /> },
    { text: t('nav.bank.statements'), path: '/app/bank/statements', icon: <ListAlt fontSize="small" /> },
    // Enable Banking connections (/app/bank/enable-banking) have no menu entry yet while the
    // channel is rolled out per tenant via admin approval (BIZ-617); reach it by URL.
  ];

  const salariesMenuItems = [
    { text: t('salaries.menu.calc'), path: '/app/salaries/calc', icon: <PointOfSale fontSize="small" /> },
    { text: t('salaries.menu.payroll'), path: '/app/salaries/payroll', icon: <Paid fontSize="small" /> },
    { text: t('salaries.menu.workers'), path: '/app/salaries/workers', icon: <People fontSize="small" /> },
    { text: t('salaries.menu.reports'), path: '/app/salaries/reports', icon: <ListAlt fontSize="small" /> },
    { text: t('salaries.menu.payments'), path: '/app/salaries/payments', icon: <Payments fontSize="small" /> },
    { text: t('salaries.menu.accounting'), path: '/app/salaries/accounting', icon: <AccountBalance fontSize="small" /> },
    { text: t('salaries.menu.messages'), path: '/app/salaries/messages', icon: <Email fontSize="small" /> },
  ];

  const timeTrackingMenuItems = [
    { text: t('nav.time_tracking.my_hours'), path: '/app/time-tracking', icon: <AccessTime fontSize="small" /> },
    { text: t('nav.time_tracking.all_entries'), path: '/app/time-tracking/all-entries', icon: <ListAlt fontSize="small" /> },
    { text: t('nav.time_tracking.week_calendar'), path: '/app/time-tracking/week', icon: <CalendarMonth fontSize="small" /> },
  ];

  // Projects module: project list + "My tasks" (assignment list) at /app/tasks. Per-project tasks: /app/projects/.../tasks.
  const projectsMenuItems = [
    { text: t('nav.projects'), path: '/app/projects', icon: <FolderSpecial fontSize="small" /> },
    { text: t('nav.tasks'), path: '/app/tasks', icon: <Assignment fontSize="small" /> },
  ];

  // Open items hub (purchasing/invoicing): overview + work queue — /app/open-items*
  const openItemsMenuItems = useMemo(() => [
    { text: t('open_items.overview', 'Overview'), path: '/app/open-items', icon: <Assignment fontSize="small" /> },
    { text: t('open_items.overview_title', 'Work queue'), path: '/app/open-items/work-queue', icon: <ListAlt fontSize="small" /> },
  ], [t]);

  const sendingServiceMenuItems = [];

  const adminMenuItems = [
    { text: t('admin.hosting_companies'), path: '/app/admin/hosts', icon: <Storage fontSize="small" /> },
    { text: t('admin.tenants'), path: '/app/admin/tenants', icon: <Business fontSize="small" /> },
    { text: t('admin.users'), path: '/app/admin/users', icon: <People fontSize="small" /> },
    { text: t('admin.access_logs'), path: '/app/admin/access-logs', icon: <History fontSize="small" /> },
    { text: t('admin.active_sessions'), path: '/app/admin/sessions', icon: <Visibility fontSize="small" /> },
    { text: t('admin.job_queue_title'), path: '/app/admin/banking/job-queue', icon: <AccountBalance fontSize="small" /> },
    { text: t('admin.database_import'), path: '/app/admin/database-import', icon: <CloudUpload fontSize="small" /> },
    { text: t('admin.voucher_correction_title'), path: '/app/admin/voucher-correction', icon: <Build fontSize="small" /> },
    { text: t('admin.payment_rematch_title'), path: '/app/admin/payment-rematch', icon: <Build fontSize="small" /> },
    { text: t('admin.close_remainder_title'), path: '/app/admin/close-remainder', icon: <CheckCircle fontSize="small" /> },
    { text: t('admin.charge_ap_correction_title'), path: '/app/admin/charge-ap-correction', icon: <Build fontSize="small" /> },
    { text: t('admin.coa_role_correction_title'), path: '/app/admin/coa-role-correction', icon: <Build fontSize="small" /> },
  ];

  // Get breadcrumb items for current page
  const getBreadcrumbs = () => {
    // Always start with Home icon (except on dashboard page itself)
    const isOnDashboard = location.pathname === '/app/dashboard';
    const homeIcon = { icon: <Home sx={{ fontSize: 18 }} />, path: '/app/dashboard', label: '' };

    // Handle dynamic routes with IDs
    const enableBankingMatch = location.pathname.match(/^\/app\/bank\/enable-banking\/([a-f0-9-]+)$/i);
    if (enableBankingMatch) {
      return [
        homeIcon,
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.enable_banking'), path: '/app/bank/enable-banking' },
        { label: itemTitle || '…', path: null }
      ];
    }

    const purchaseInvoiceMatch = location.pathname.match(/^\/app\/purchase-invoices\/(new|[a-f0-9-]+)$/i);
    if (purchaseInvoiceMatch) {
      const idOrNew = purchaseInvoiceMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : itemTitle;
      return [
        homeIcon,
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.purchase_invoices'), path: '/app/purchase-invoices' },
        { label: action, path: null }
      ];
    }

    const chargeMatch = location.pathname.match(/^\/app\/charges\/(new|[a-f0-9-]+)$/i);
    if (chargeMatch) {
      const idOrNew = chargeMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.charges'), path: '/app/charges' },
        { label: action, path: null }
      ];
    }

    const salesOrderMatch = location.pathname.match(/^\/app\/sales-orders\/(new|[a-f0-9-]+)$/i);
    if (salesOrderMatch) {
      const idOrNew = salesOrderMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : itemTitle;
      return [
        homeIcon,
        { label: t('nav.sales'), path: '/app/sales-orders' },
        { label: t('nav.sales_orders'), path: '/app/sales-orders' },
        { label: action, path: null }
      ];
    }

    const purchaseOrderMatch = location.pathname.match(/^\/app\/purchase-orders\/(new|[a-f0-9-]+)$/i);
    if (purchaseOrderMatch) {
      const idOrNew = purchaseOrderMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : itemTitle;
      return [
        homeIcon,
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.purchase_orders'), path: '/app/purchase-orders' },
        { label: action, path: null }
      ];
    }

    const invoiceMatch = location.pathname.match(/^\/app\/invoices\/(new|[a-f0-9-]+)$/i);
    if (invoiceMatch) {
      const idOrNew = invoiceMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : itemTitle;
      return [
        homeIcon,
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.invoices'), path: '/app/invoices' },
        { label: action, path: null }
      ];
    }

    const contractInvoiceMatch = location.pathname.match(/^\/app\/contract-invoices\/(new|[a-f0-9-]+)$/i);
    if (contractInvoiceMatch) {
      const idOrNew = contractInvoiceMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.contract_invoices'), path: '/app/contract-invoices' },
        { label: action, path: null }
      ];
    }

    const paymentMatch = location.pathname.match(/^\/app\/payments\/(new|[a-f0-9-]+)$/i);
    if (paymentMatch) {
      const idOrNew = paymentMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.payments'), path: '/app/payments' },
        { label: action, path: null }
      ];
    }

    const exchangeRateMatch = location.pathname.match(/^\/app\/exchange-rates\/([a-z]{3})$/i);
    if (exchangeRateMatch) {
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.exchange_rates'), path: '/app/exchange-rates' },
        { label: exchangeRateMatch[1].toUpperCase(), path: null }
      ];
    }

    const voucherMatch = location.pathname.match(/^\/app\/vouchers\/([0-9a-f-]+)$/);
    if (voucherMatch) {
      return [
        homeIcon,
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.vouchers'), path: '/app/vouchers' },
        { label: itemTitle || voucherMatch[1].substring(0, 8), path: null }
      ];
    }

    const contactMatch = location.pathname.match(/^\/app\/contacts\/(new|[a-f0-9-]+)$/i);
    if (contactMatch) {
      const idOrNew = contactMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || `#${idOrNew}`);
      return [
        homeIcon,
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.contacts'), path: '/app/contacts' },
        { label: action, path: null }
      ];
    }

    const leadMatch = location.pathname.match(/^\/app\/leads\/(new|[a-f0-9-]+)$/i);
    if (leadMatch) {
      const idOrNew = leadMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.leads'), path: '/app/leads' },
        { label: action, path: null }
      ];
    }

    const dealMatch = location.pathname.match(/^\/app\/deals\/(new|[a-f0-9-]+)$/i);
    if (dealMatch) {
      const idOrNew = dealMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.deals'), path: '/app/deals' },
        { label: action, path: null }
      ];
    }

    const activityMatch = location.pathname.match(/^\/app\/activities\/(new|[a-f0-9-]+)$/i);
    if (activityMatch) {
      const idOrNew = activityMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.activities'), path: '/app/activities' },
        { label: action, path: null }
      ];
    }

    const taskMatch = location.pathname.match(/^\/app\/projects\/([a-f0-9-]+)\/tasks\/(new|[a-f0-9-]+)$/i);
    if (taskMatch) {
      const projectId = taskMatch[1];
      const idOrNew = taskMatch[2];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.projects'), path: '/app/projects' },
        { label: parentTitle || t('tasks.title'), path: `/app/projects/${projectId}/tasks` },
        { label: action, path: null }
      ];
    }

    const taskSettingsMatch = location.pathname.match(/^\/app\/projects\/([a-f0-9-]+)\/tasks\/settings$/i);
    if (taskSettingsMatch) {
      const projectId = taskSettingsMatch[1];
      return [
        homeIcon,
        { label: t('nav.projects'), path: '/app/projects' },
        { label: t('tasks.title'), path: `/app/projects/${projectId}/tasks` },
        { label: t('task_stages.title'), path: null }
      ];
    }

    const taskListMatch = location.pathname.match(/^\/app\/projects\/([a-f0-9-]+)\/tasks$/i);
    if (taskListMatch) {
      return [
        homeIcon,
        { label: t('nav.projects'), path: '/app/projects' },
        { label: itemTitle || t('tasks.title'), path: null }
      ];
    }

    const projectMatch = location.pathname.match(/^\/app\/projects\/(new|[a-f0-9-]+)$/i);
    if (projectMatch) {
      const idOrNew = projectMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.projects'), path: '/app/projects' },
        { label: action, path: null }
      ];
    }

    const coaMatch = location.pathname.match(/^\/app\/coa\/(new|[a-f0-9-]+)$/i);
    if (coaMatch) {
      const idOrNew = coaMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_configuration'), path: '/app/coa-configuration' },
        { label: action, path: null }
      ];
    }

    const vatMatch = location.pathname.match(/^\/app\/vat\/(new|[a-f0-9-]+)$/i);
    if (vatMatch) {
      const idOrNew = vatMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.vat_configuration'), path: '/app/vat-configuration' },
        { label: action, path: null }
      ];
    }

    const financialYearMatch = location.pathname.match(/^\/app\/financial-years\/(new|[a-f0-9-]+)$/i);
    if (financialYearMatch) {
      const idOrNew = financialYearMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.financial_years_configuration'), path: '/app/financial-years-configuration' },
        { label: action, path: null }
      ];
    }

    const voucherTypeMatch = location.pathname.match(/^\/app\/voucher-types\/(new|[a-f0-9-]+)$/i);
    if (voucherTypeMatch) {
      const idOrNew = voucherTypeMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.voucher_types_configuration'), path: '/app/voucher-types-configuration' },
        { label: action, path: null }
      ];
    }

    const dimensionMatch = location.pathname.match(/^\/app\/dimensions\/(new|[a-f0-9-]+)$/i);
    if (dimensionMatch) {
      const idOrNew = dimensionMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.dimensions_configuration'), path: '/app/dimensions-configuration' },
        { label: action, path: null }
      ];
    }

    const currencyMatch = location.pathname.match(/^\/app\/currencies\/(new|[a-f0-9-]+)$/i);
    if (currencyMatch) {
      const idOrNew = currencyMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.currencies_configuration'), path: '/app/currencies-configuration' },
        { label: action, path: null }
      ];
    }

    const coaRoleConfigMatch = location.pathname.match(/^\/app\/coa-role-config\/(new|[a-f0-9-]+)$/i);
    if (coaRoleConfigMatch) {
      const idOrNew = coaRoleConfigMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_role_configs_configuration'), path: '/app/coa-role-configs-configuration' },
        { label: action, path: null }
      ];
    }

    const coaGroupMatch = location.pathname.match(/^\/app\/coa-groups\/(new|[a-f0-9-]+)$/i);
    if (coaGroupMatch) {
      const idOrNew = coaGroupMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_groups_configuration'), path: '/app/coa-groups-configuration' },
        { label: action, path: null }
      ];
    }

    const paymentTermMatch = location.pathname.match(/^\/app\/payment-terms\/(new|[0-9a-f-]+)$/);
    if (paymentTermMatch) {
      const idOrNew = paymentTermMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : t('common.edit') ;
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.payment_terms_configuration'), path: '/app/payment-terms-configuration' },
        { label: action, path: null }
      ];
    }

    const employeeMatch = location.pathname.match(/^\/app\/employees\/(new|[a-f0-9-]+)$/i);
    if (employeeMatch) {
      const idOrNew = employeeMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.employees'), path: '/app/employees' },
        { label: action, path: null }
      ];
    }

    const expenseMatch = location.pathname.match(/^\/app\/expenses\/(new|[a-f0-9-]+)$/i);
    if (expenseMatch) {
      const idOrNew = expenseMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.expenses'), path: '/app/expenses' },
        { label: action, path: null }
      ];
    }

    const expenseCategoryMatch = location.pathname.match(/^\/app\/expense-categories\/(new|[a-f0-9-]+)$/i);
    if (expenseCategoryMatch) {
      const idOrNew = expenseCategoryMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.expenses'), path: '/app/expenses' },
        { label: t('nav.expense_categories_configuration'), path: '/app/expense-categories' },
        { label: action, path: null }
      ];
    }

    const departmentMatch = location.pathname.match(/^\/app\/departments\/(new|[a-f0-9-]+)$/i);
    if (departmentMatch) {
      const idOrNew = departmentMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.departments'), path: '/app/departments' },
        { label: action, path: null }
      ];
    }

    const paymentMethodMatch = location.pathname.match(/^\/app\/payment-methods\/(new|[a-f0-9-]+)$/i);
    if (paymentMethodMatch) {
      const idOrNew = paymentMethodMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.payment_methods_configuration'), path: '/app/payment-methods-configuration' },
        { label: action, path: null }
      ];
    }

    const productMatch = location.pathname.match(/^\/app\/products\/(new|[a-f0-9-]+)$/i);
    if (productMatch) {
      const idOrNew = productMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.products'), path: '/app/products' },
        { label: action, path: null }
      ];
    }

    const meteredPeriodMatch = location.pathname.match(/^\/app\/metered-billing\/periods\/([a-f0-9-]+)$/i);
    if (meteredPeriodMatch) {
      return [
        homeIcon,
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('metered_billing.periods'), path: '/app/metered-billing/periods' },
        { label: itemTitle || t('common.edit'), path: null }
      ];
    }

    const meteredSubjectMatch = location.pathname.match(/^\/app\/metered-billing\/subjects\/(new|[a-f0-9-]+)$/i);
    if (meteredSubjectMatch) {
      const idOrNew = meteredSubjectMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true) },
        { label: t('metered_billing.subjects'), path: '/app/metered-billing/subjects' },
        { label: action, path: null }
      ];
    }

    const stockMatch = location.pathname.match(/^\/app\/stocks\/(new|[a-f0-9-]+)$/i);
    if (stockMatch) {
      const idOrNew = stockMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.stocks'), path: '/app/stocks' },
        { label: action, path: null }
      ];
    }

    const unitMatch = location.pathname.match(/^\/app\/units\/(new|[a-f0-9-]+)$/i);
    if (unitMatch) {
      const idOrNew = unitMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.units_configuration'), path: '/app/units-configuration' },
        { label: action, path: null }
      ];
    }

    const productGroupMatch = location.pathname.match(/^\/app\/product-groups\/(new|[a-f0-9-]+)$/i);
    if (productGroupMatch) {
      const idOrNew = productGroupMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.product_groups'), path: '/app/product-groups' },
        { label: action, path: null }
      ];
    }

    // Admin routes
    const adminHostMatch = location.pathname.match(/^\/app\/admin\/hosts\/(new|[a-f0-9-]+)$/i);
    if (adminHostMatch) {
      const idOrNew = adminHostMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.hosting_companies'), path: '/app/admin/hosts' },
        { label: action, path: null }
      ];
    }

    const adminTenantMatch = location.pathname.match(/^\/app\/admin\/tenants\/(new|[a-f0-9-]+)$/i);
    if (adminTenantMatch) {
      const idOrNew = adminTenantMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.tenants'), path: '/app/admin/tenants' },
        { label: action, path: null }
      ];
    }

    const adminUserMatch = location.pathname.match(/^\/app\/admin\/users\/(new|[a-f0-9-]+)$/i);
    if (adminUserMatch) {
      const idOrNew = adminUserMatch[1];
      const action = idOrNew === 'new' ? t('common.new') : (itemTitle || t('common.edit'));
      return [
        homeIcon,
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.users'), path: '/app/admin/users' },
        { label: action, path: null }
      ];
    }

    // Knowledgebase entry — the KB is global, so no tenant scoping in the URL.
    // View (/entries/:id) shows the entry's question; form (/new or /entries/:id/edit) shows a
    // stable "New"/"Edit" label so the crumb reads correctly even before the entry loads. In
    // edit mode we also insert the entry title as a clickable crumb back to the read view.
    const kbEntryEditMatch = location.pathname.match(/^\/app\/knowledgebase\/entries\/([a-f0-9-]+)\/edit$/i);
    if (kbEntryEditMatch) {
      const entryId = kbEntryEditMatch[1];
      return [
        homeIcon,
        { label: t('nav.knowledgebase', 'Tietopankki'), path: '/app/knowledgebase' },
        { label: itemTitle || '…', path: `/app/knowledgebase/entries/${entryId}` },
        { label: t('common.edit'), path: null }
      ];
    }
    if (/^\/app\/knowledgebase\/entries\/new$/i.test(location.pathname)) {
      return [
        homeIcon,
        { label: t('nav.knowledgebase', 'Tietopankki'), path: '/app/knowledgebase' },
        { label: t('common.new'), path: null }
      ];
    }
    const kbEntryViewMatch = location.pathname.match(/^\/app\/knowledgebase\/entries\/[a-f0-9-]+$/i);
    if (kbEntryViewMatch) {
      return [
        homeIcon,
        { label: t('nav.knowledgebase', 'Tietopankki'), path: '/app/knowledgebase' },
        { label: itemTitle || '…', path: null }
      ];
    }

    // Map pages to their breadcrumb structure
    const pageStructure = {
      '/app/dashboard': [
        { label: t('nav.dashboard'), path: null, icon: <Home sx={{ fontSize: 18 }} /> }
      ],
      '/app/activity': [
        { label: t('activity.title', { defaultValue: 'Activity' }), path: null }
      ],
      '/app/settings': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)}
      ],
      // Invoicing
      '/app/invoicing': [
        { label: t('nav.invoicing'), path: null }
      ],
      '/app/invoices': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.invoices'), path: null }
      ],
      '/app/metered-billing/periods': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('metered_billing.periods'), path: null }
      ],
      '/app/metered-billing/subjects': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true) },
        { label: t('metered_billing.subjects'), path: null }
      ],
      '/app/transfer-invoices': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.transfer_invoices'), path: null }
      ],
      '/app/contract-invoices': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.contract_invoices'), path: null }
      ],
      '/app/bulk-invoicing': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.bulk_invoicing'), path: null }
      ],
      '/app/bulk-invoicing/by-product': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.bulk_invoicing'), path: '/app/bulk-invoicing' },
        { label: t('bulk_invoicing.by_product'), path: null }
      ],
      '/app/bulk-invoicing/water': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.bulk_invoicing'), path: '/app/bulk-invoicing' },
        { label: t('water_billing.title'), path: null }
      ],
      '/app/bulk-invoicing/metered': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.bulk_invoicing'), path: '/app/bulk-invoicing' },
        { label: t('metered_product.title'), path: null }
      ],
      '/app/payment-notifications': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.payment_notifications'), path: null }
      ],
      // Sales
      '/app/sales-orders': [
        { label: t('nav.sales'), path: '/app/sales-orders' },
        { label: t('nav.sales_orders'), path: null }
      ],
      '/app/sales-reports': [
        { label: t('nav.sales'), path: '/app/sales-orders' },
        { label: t('nav.reports'), path: null }
      ],
      // Purchasing
      '/app/purchasing': [
        { label: t('nav.purchasing'), path: null }
      ],
      '/app/purchase-invoices': [
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.purchase_invoices'), path: null }
      ],
      '/app/purchase-orders': [
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.purchase_orders'), path: null }
      ],
      '/app/charges': [
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.charges'), path: null }
      ],
      '/app/purchasing-reports': [
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.reports'), path: null }
      ],
      // CRM
      '/app/crm': [
        { label: t('nav.crm'), path: null }
      ],
      '/app/contacts': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.contacts'), path: null }
      ],
      '/app/contact-groups': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.contact_groups'), path: null }
      ],
      '/app/activities': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.activities'), path: null }
      ],
      '/app/leads': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.leads'), path: null }
      ],
      '/app/deals': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.deals'), path: null }
      ],
      '/app/crm-reports': [
        { label: t('nav.crm'), path: '/app/crm' },
        { label: t('nav.reports'), path: null }
      ],
      // Inventory
      '/app/inventory': [
        { label: t('nav.inventory'), path: null }
      ],
      '/app/products': [
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.products'), path: null }
      ],
      '/app/stocks': [
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.stocks'), path: null }
      ],
      '/app/units-configuration': [
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.units_configuration'), path: null }
      ],
      '/app/product-groups': [
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.product_groups'), path: null }
      ],
      '/app/inventory-reports': [
        { label: t('nav.inventory'), path: '/app/inventory' },
        { label: t('nav.reports'), path: null }
      ],
      // Accounting
      '/app/accounting': [
        { label: t('nav.accounting'), path: null }
      ],
      '/app/payments': [
        { label: t('nav.invoicing'), path: '/app/invoicing' },
        { label: t('nav.payments'), path: null }
      ],
      '/app/vouchers': [
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.vouchers'), path: null }
      ],
      '/app/talousarvio': [
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.talousarvio', 'Talousarvio'), path: null }
      ],
      '/app/ledger-browser': [
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.ledger_browser', 'Kirjanpidon selailu'), path: null }
      ],
      '/app/account-matching': [
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.account_matching', 'Kohdistus tilin sisällä'), path: null }
      ],
      '/app/accounting-reports': [
        { label: t('nav.accounting'), path: '/app/accounting' },
        { label: t('nav.reports'), path: null }
      ],
      '/app/vat-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.vat_configuration'), path: null }
      ],
      '/app/coa-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_configuration'), path: null }
      ],
      '/app/financial-years-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.financial_years_configuration'), path: null }
      ],
      '/app/voucher-types-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.voucher_types_configuration'), path: null }
      ],
      '/app/entry-types-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.entry_types_configuration'), path: null }
      ],
      '/app/dimensions-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.dimensions_configuration'), path: null }
      ],
      '/app/currencies-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.currencies_configuration'), path: null }
      ],
      '/app/exchange-rates': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.exchange_rates'), path: null }
      ],
      '/app/coa-role-configs-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_role_configs_configuration'), path: null }
      ],
      '/app/coa-groups-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.coa_groups_configuration'), path: null }
      ],
      '/app/payment-terms-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.payment_terms_configuration'), path: null }
      ],
      '/app/payment-methods-configuration': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.payment_methods_configuration'), path: null }
      ],
      '/app/expenses': [
        { label: t('nav.expenses'), path: null }
      ],
      '/app/expense-categories': [
        { label: t('nav.expenses'), path: '/app/expenses' },
        { label: t('nav.expense_categories_configuration'), path: null }
      ],
      '/app/company-settings': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('company_settings.title'), path: null }
      ],
      '/app/purchasing/settings': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.purchasing'), path: null },
        { label: t('purchasing.settings'), path: null }
      ],
      '/app/user-groups': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('settings.user_groups'), path: null }
      ],
      '/app/purchasing/approval-rules': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.purchasing'), path: null },
        { label: t('settings.purchase_invoice_approval_rules'), path: null }
      ],
      '/app/purchasing/pending-approvals': [
        { label: t('nav.purchasing'), path: '/app/purchasing' },
        { label: t('nav.pending_approvals'), path: null }
      ],
      '/app/export-data': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.export_data'), path: null }
      ],
      '/app/employees': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.employees'), path: null }
      ],
      '/app/departments': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.departments'), path: null }
      ],
      // Reports
      '/app/report-designer': [
        { label: t('nav.reports'), path: null },
        { label: t('nav.report_designer'), path: null },
      ],
      '/app/report-viewer': [
        { label: t('nav.reports'), path: null },
        { label: t('report_templates.preview'), path: null },
      ],
      // Sending Service
      '/app/sending-service': [
        { label: t('nav.sending_service'), path: null }
      ],
      // Bank
      '/app/bank': [
        { label: t('nav.bank'), path: null }
      ],
      '/app/bank/payments': [
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.payments'), path: null },
      ],
      '/app/bank/payments/overview': [
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.payments'), path: '/app/bank/payments' },
        { label: t('nav.bank.overview'), path: null },
      ],
      '/app/bank/payments/rebuild': [
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.payments'), path: '/app/bank/payments' },
        { label: t('nav.bank.rebuild', { defaultValue: 'Korjaa kohdistus' }), path: null },
      ],
      '/app/bank/statements': [
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.statements'), path: null },
      ],
      '/app/bank/enable-banking': [
        { label: t('nav.bank'), path: '/app/bank' },
        { label: t('nav.bank.enable_banking'), path: null },
      ],
      '/app/settings/si-messages': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.si_messages'), path: null },
      ],
      '/app/settings/ri-messages': [
        { label: t('nav.settings'), onClick: () => setSettingsDialogOpen(true)},
        { label: t('nav.ri_messages'), path: null },
      ],
      // Salaries
      '/app/salaries': [
        { label: t('nav.salaries'), path: null }
      ],
      // Time Tracking
      '/app/time-tracking/all-entries': [
        { label: t('nav.time_tracking'), path: '/app/time-tracking' },
        { label: t('nav.time_tracking.all_entries'), path: null }
      ],
      '/app/time-tracking/week': [
        { label: t('nav.time_tracking'), path: '/app/time-tracking' },
        { label: t('nav.time_tracking.week_calendar'), path: null }
      ],
      '/app/time-tracking': [
        { label: t('nav.time_tracking'), path: null }
      ],
      '/app/open-items': [
        { label: t('open_items.title', 'Accounting firm'), path: null }
      ],
      '/app/open-items/work-queue': [
        { label: t('open_items.title', 'Accounting firm'), path: '/app/open-items' },
        { label: t('open_items.overview_title', 'Work queue'), path: null }
      ],
      '/app/board': [
        { label: t('board.title', 'Board'), path: null }
      ],
      '/app/tasks': [
        { label: t('nav.projects'), path: '/app/projects' },
        { label: t('nav.tasks'), path: null }
      ],
      // Stages Matrix
      '/app/projects/stages-matrix': [
        { label: t('nav.projects'), path: '/app/projects' },
        { label: t('nav.stages_matrix'), path: null }
      ],
      // Tags
      '/app/projects/tags': [
        { label: t('nav.projects'), path: '/app/projects' },
        { label: t('nav.tags'), path: null }
      ],
      // Projects
      '/app/projects': [
        { label: t('nav.projects'), path: null }
      ],
      // Knowledgebase
      '/app/knowledgebase': [
        { label: t('nav.knowledgebase', 'Tietopankki'), path: null }
      ],
      // Helpdesk
      '/app/helpdesk/tickets': [
        { label: t('nav.helpdesk'), path: null },
        { label: t('nav.helpdesk_tickets'), path: null }
      ],
      '/app/helpdesk/categories': [
        { label: t('nav.helpdesk'), path: '/app/helpdesk/tickets' },
        { label: t('nav.helpdesk_categories'), path: null }
      ],
      // Business Intelligence
      '/app/bi': [
        { label: t('bi.title'), path: null }
      ],
      // Admin
      '/app/admin': [
        { label: t('admin.admin'), path: null }
      ],
      '/app/admin/hosts': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.hosting_companies'), path: null }
      ],
      '/app/admin/tenants': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.tenants'), path: null }
      ],
      '/app/admin/users': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.users'), path: null }
      ],
      '/app/admin/access-logs': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.access_logs'), path: null }
      ],
      '/app/admin/sessions': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.active_sessions'), path: null }
      ],
      '/app/admin/database-import': [
        { label: t('admin.admin'), path: '/app/admin' },
        { label: t('admin.database_import'), path: null }
      ],
    };

    // Find matching page by checking if pathname starts with the key
    // Sort keys by length (longest first) so more specific paths match first
    const sortedPaths = Object.keys(pageStructure).sort((a, b) => b.length - a.length);
    const matchedPath = sortedPaths.find(path =>
      location.pathname.startsWith(path)
    );

    if (matchedPath) {
      const breadcrumbs = pageStructure[matchedPath];
      // Add home icon at the beginning if not on dashboard
      return isOnDashboard ? breadcrumbs : [homeIcon, ...breadcrumbs];
    }

    // Default fallback
    return [{ label: t('nav.dashboard'), path: null, icon: <Home sx={{ fontSize: 18 }} /> }];
  };

  // Reflect "tenant · current page" in the browser tab so users juggling multiple
  // tenants across tabs can see which company is open in each. Falls back to a
  // plain product title when nothing useful is known yet (e.g. before tenant load).
  useEffect(() => {
    const crumbs = getBreadcrumbs();
    const lastLabel = [...crumbs].reverse().map((c) => c?.label).find((label) => typeof label === 'string' && label.trim());
    const tenantName = currentTenant?.name?.trim();
    const parts = [];
    if (tenantName) parts.push(tenantName);
    if (lastLabel) parts.push(lastLabel);
    document.title = parts.length ? parts.join(' · ') : 'Viilu Talous';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, itemTitle, parentTitle, currentTenant?.name, t, i18n.language]);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSettings = () => {
    handleUserMenuClose();
    navigate('/app/settings');
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const handleTenantMenuOpen = (event) => {
    if (tenants.length > 1) {
      setTenantMenuAnchor(event.currentTarget);
    }
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchor(null);
    setTenantSearch('');
  };

  const handleTenantSelect = (tenant) => {
    selectTenant(tenant);
    handleTenantMenuClose();

    // Rewrite the URL slug to the newly selected tenant and, if on a detail
    // view (`/.../<uuid>` or `/.../new`), drop back to the list view since the
    // entity ID is tenant-scoped and won't resolve under the new tenant.
    // Use rawLocation (slug intact) — `location` is slug-stripped, and slice(2)
    // below assumes the `<slug>` segment is present, so using the stripped path
    // would drop a real segment (e.g. `/settings/`) and break the route (VII-390).
    const parts = rawLocation.pathname.split('/').filter(Boolean); // ['app','<slug>','seg','seg', ...]
    if (parts.length >= 2 && parts[0] === 'app' && tenant.slug) {
      const isUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
      const isNew = (s) => s === 'new';
      let tail = parts.slice(2); // segments after /app/<oldSlug>
      // Admin tools that encode a *customer* tenant id in the path — reset
      // to the picker when switching the app session tenant.
      const voucherCorrectionIdx = tail.indexOf('voucher-correction');
      const paymentRematchIdx = tail.indexOf('payment-rematch');
      const closeRemainderIdx = tail.indexOf('close-remainder');
      const chargeApIdx = tail.indexOf('charge-ap-correction');
      const coaRoleIdx = tail.indexOf('coa-role-correction');
      if (voucherCorrectionIdx >= 0) {
        tail = tail.slice(0, voucherCorrectionIdx + 1);
      } else if (paymentRematchIdx >= 0) {
        tail = tail.slice(0, paymentRematchIdx + 1);
      } else if (closeRemainderIdx >= 0) {
        tail = tail.slice(0, closeRemainderIdx + 1);
      } else if (chargeApIdx >= 0) {
        tail = tail.slice(0, chargeApIdx + 1);
      } else if (coaRoleIdx >= 0) {
        tail = tail.slice(0, coaRoleIdx + 1);
      } else if (tail.length && (isUuid(tail[tail.length - 1]) || isNew(tail[tail.length - 1]))) {
        tail = tail.slice(0, -1);
      }
      const target = '/app/' + tenant.slug + (tail.length ? '/' + tail.join('/') : '');
      rawNavigate(target);
    }
  };

  const handleNavigation = (path) => {
    if (typeof path === 'string' && (path === '/me' || path.startsWith('/me/'))) {
      rawNavigate(path);
    } else {
      navigate(path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const closeAllMenus = (except) => {
    if (except !== 'invoicing') setInvoicingOpen(false);
    if (except !== 'sales') setSalesOpen(false);
    if (except !== 'purchasing') setPurchasingOpen(false);
    if (except !== 'expenses') setExpensesOpen(false);
    if (except !== 'crm') setCrmOpen(false);
    if (except !== 'inventory') setInventoryOpen(false);
    if (except !== 'accounting') setAccountingOpen(false);
    if (except !== 'bank') setBankOpen(false);
    if (except !== 'salaries') setSalariesOpen(false);
    if (except !== 'timeTracking') setTimeTrackingOpen(false);
    if (except !== 'projects') setProjectsOpen(false);
    if (except !== 'openItems') setOpenItemsOpen(false);
    if (except !== 'sendingService') setSendingServiceOpen(false);
    if (except !== 'admin') setAdminOpen(false);
  };

  const handleInvoicingClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, invoicingMenuItems, t('nav.invoicing'));
    } else {
      closeAllMenus('invoicing');
      setInvoicingOpen(true);
      navigate('/app/invoicing');
    }
  };

  const handleInvoicingHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, invoicingMenuItems, t('nav.invoicing'));
    }
  };

  const handleSalesClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, salesMenuItems, t('nav.sales'));
    } else {
      closeAllMenus('sales');
      setSalesOpen(true);
      navigate('/app/sales-orders');
    }
  };

  const handleSalesHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, salesMenuItems, t('nav.sales'));
    }
  };

  const handlePurchasingClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, purchasingMenuItems, t('nav.purchasing'));
    } else {
      closeAllMenus('purchasing');
      setPurchasingOpen(true);
      navigate('/app/purchasing');
    }
  };

  const handlePurchasingHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, purchasingMenuItems, t('nav.purchasing'));
    }
  };

  const handleExpensesClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, expensesMenuItems, t('nav.expenses'));
    } else {
      closeAllMenus('expenses');
      setExpensesOpen(true);
      navigate('/app/expenses');
    }
  };

  const handleExpensesHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, expensesMenuItems, t('nav.expenses'));
    }
  };

  const handleCrmClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, crmMenuItems, t('nav.crm'));
    } else {
      closeAllMenus('crm');
      setCrmOpen(true);
      navigate('/app/crm');
    }
  };

  const handleCrmHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, crmMenuItems, t('nav.crm'));
    }
  };

  const handleInventoryClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, inventoryMenuItems, t('nav.inventory'));
    } else {
      closeAllMenus('inventory');
      setInventoryOpen(true);
      navigate('/app/inventory');
    }
  };

  const handleInventoryHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, inventoryMenuItems, t('nav.inventory'));
    }
  };

  const handleAccountingClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, accountingMenuItems, t('nav.accounting'));
    } else {
      closeAllMenus('accounting');
      setAccountingOpen(true);
      navigate('/app/accounting');
    }
  };

  const handleAccountingHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, accountingMenuItems, t('nav.accounting'));
    }
  };

  const handleBankClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, bankMenuItems, t('nav.bank'));
    } else {
      closeAllMenus('bank');
      setBankOpen(true);
      navigate('/app/bank');
    }
  };

  const handleBankHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, bankMenuItems, t('nav.bank'));
    }
  };

  const handleSalariesClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, salariesMenuItems, t('nav.salaries'));
    } else {
      closeAllMenus('salaries');
      setSalariesOpen(true);
      navigate('/app/salaries/home');
    }
  };

  const handleSalariesHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, salariesMenuItems, t('nav.salaries'));
    }
  };

  const handleTimeTrackingClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, timeTrackingMenuItems, t('nav.time_tracking'));
    } else {
      closeAllMenus('timeTracking');
      setTimeTrackingOpen(true);
      navigate('/app/time-tracking');
    }
  };

  const handleTimeTrackingHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, timeTrackingMenuItems, t('nav.time_tracking'));
    }
  };

  const handleProjectsClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, projectsMenuItems, t('nav.projects'));
    } else {
      closeAllMenus('projects');
      setProjectsOpen(true);
      navigate('/app/projects');
    }
  };

  const handleProjectsHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, projectsMenuItems, t('nav.projects'));
    }
  };

  const handleOpenItemsClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, openItemsMenuItems, t('open_items.title', 'Accounting firm'));
    } else {
      closeAllMenus('openItems');
      setOpenItemsOpen(true);
      navigate('/app/open-items');
    }
  };

  const handleOpenItemsHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, openItemsMenuItems, t('open_items.title', 'Accounting firm'));
    }
  };

  const handleSendingServiceClick = () => {
    navigate('/app/sending-service');
  };

  const handleSendingServiceHover = () => {};

  const handleSettingsClick = () => {
    setSettingsDialogOpen(true);
  };

  const handleAdminClick = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, adminMenuItems, t('admin.admin'));
    } else {
      closeAllMenus('admin');
      setAdminOpen(true);
      navigate('/app/admin');
    }
  };

  const handleAdminHover = (event) => {
    if (sidebarCollapsed) {
      handlePopoverOpen(event, adminMenuItems, t('admin.admin'));
    }
  };

  const isAdminActive = () => location.pathname.startsWith('/app/admin');

  const handlePopoverClose = () => {
    const timeout = setTimeout(() => {
      setPopoverAnchor(null);
      setPopoverContent([]);
      setPopoverTitle('');
    }, 100);
    setPopoverTimeout(timeout);
  };

  const handlePopoverOpen = (event, menuItems, title) => {
    if (popoverTimeout) {
      clearTimeout(popoverTimeout);
      setPopoverTimeout(null);
    }
    setPopoverAnchor(event.currentTarget);
    setPopoverContent(menuItems);
    setPopoverTitle(title);
  };

  const handlePopoverMouseEnter = () => {
    if (popoverTimeout) {
      clearTimeout(popoverTimeout);
      setPopoverTimeout(null);
    }
  };

  const handlePopoverItemClick = (path) => {
    if (popoverTimeout) {
      clearTimeout(popoverTimeout);
      setPopoverTimeout(null);
    }
    handleNavigation(path);
    setPopoverAnchor(null);
    setPopoverContent([]);
    setPopoverTitle('');
  };

  const isActive = (path) => location.pathname.startsWith(path);
  const isExpensesActive = () => location.pathname.startsWith('/app/expenses') || location.pathname.startsWith('/app/expense-categories');
  const isInvoicingActive = () =>
    location.pathname.startsWith('/app/invoicing') ||
    location.pathname.startsWith('/app/invoices') ||
    location.pathname.startsWith('/app/bulk-invoicing') ||
    location.pathname.startsWith('/app/contract-invoices') ||
    location.pathname.startsWith('/app/payments') ||
    location.pathname.startsWith('/app/invoice-reports') ||
    location.pathname.startsWith('/app/invoice-report') ||
    location.pathname.startsWith('/app/payment-notifications') ||
    location.pathname.startsWith('/app/metered-billing');
  const isSalesActive = () => location.pathname.startsWith('/app/sales-orders') || location.pathname.startsWith('/app/sales-reports');
  const isPurchasingActive = () => location.pathname.startsWith('/app/purchasing') || location.pathname.startsWith('/app/purchase-invoices') || location.pathname.startsWith('/app/purchase-orders') || location.pathname.startsWith('/app/charges')
    || location.pathname.startsWith('/app/purchasing-reports');
  const isCrmActive = () => location.pathname.startsWith('/app/crm') || location.pathname.startsWith('/app/contacts') || location.pathname.startsWith('/app/contact-groups') || location.pathname.startsWith('/app/activities') || location.pathname.startsWith('/app/leads') || location.pathname.startsWith('/app/deals');
  const isInventoryActive = () => location.pathname.startsWith('/app/inventory') || location.pathname.startsWith('/app/products') || location.pathname.startsWith('/app/stocks')
    || location.pathname.startsWith('/app/units') || location.pathname.startsWith('/app/product-groups') || location.pathname.startsWith('/app/inventory-reports');
  const isAccountingActive = () => location.pathname.startsWith('/app/accounting') || location.pathname.startsWith('/app/vouchers') || location.pathname.startsWith('/app/talousarvio') || location.pathname.startsWith('/app/ledger-browser') || location.pathname.startsWith('/app/account-matching') || location.pathname.startsWith('/app/vat') || location.pathname.startsWith('/app/coa') || location.pathname.startsWith('/app/reports');
  const isBankActive = () => location.pathname.startsWith('/app/bank');
  const isSalariesActive = () => location.pathname.startsWith('/app/salaries');
  const isBIActive = () => location.pathname.startsWith('/app/bi');
  const isTimeTrackingActive = () => location.pathname.startsWith('/app/time-tracking');
  const isMyTasksActive = () => (
    location.pathname === '/app/tasks' || location.pathname.startsWith('/app/tasks/')
  );
  const isOpenItemsPageActive = () => location.pathname.startsWith('/app/open-items');
  const isBoardPageActive = () => location.pathname.startsWith('/app/board');
  const isProjectsActive = () => location.pathname.startsWith('/app/projects');
  const isHelpdeskActive = () => location.pathname.startsWith('/app/helpdesk');
  const isKnowledgebaseActive = () => location.pathname.startsWith('/app/knowledgebase');
  const isSendingServiceActive = () => location.pathname.startsWith('/app/sending-service');
  const isSettingsActive = () => location.pathname.startsWith('/app/company-settings') || location.pathname.startsWith('/app/vat-configuration') || location.pathname.startsWith('/app/coa-configuration') || location.pathname.startsWith('/app/financial-years') || location.pathname.startsWith('/app/voucher-types') || location.pathname.startsWith('/app/cost-centres') || location.pathname.startsWith('/app/currencies') || location.pathname.startsWith('/app/payment-terms') || location.pathname.startsWith('/app/payment-methods') || location.pathname.startsWith('/app/units-configuration') || location.pathname.startsWith('/app/employees') || location.pathname.startsWith('/app/departments') || location.pathname.startsWith('/app/expense-categories') || location.pathname.startsWith('/app/export-data') || location.pathname.startsWith('/app/coa-role-configs') || location.pathname.startsWith('/app/coa-groups') || location.pathname.startsWith('/app/transfer-invoices');

  const drawer = (
    <>
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          minHeight: 64,
          borderBottom: 1,
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => handleNavigation('/app/dashboard')}
      >
        {sidebarCollapsed ? (
          <ViiluIcon size={32} />
        ) : (
          <ViiluLogo height={30} />
        )}
      </Box>
      <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List sx={{ '& .MuiListItemButton-root': { py: 0.4 }, '& .MuiListItemIcon-root': { minHeight: 0 } }}>
          {/* Dashboard */}
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('nav.dashboard') : ''} placement="right">
              <ListItemButton
                onClick={() => handleNavigation('/app/dashboard')}
                selected={isActive('/app/dashboard')}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Dashboard /></ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('nav.dashboard')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Open items / Accountant — overview + work queue */}
          {isModuleEnabled('accounting_firm') && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleOpenItemsClick}
                  onMouseEnter={handleOpenItemsHover}
                  onMouseLeave={handlePopoverClose}
                  selected={isOpenItemsPageActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Assignment /></ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('open_items.title', 'Accounting firm')} />}
                  {!sidebarCollapsed && (openItemsOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              <Collapse in={openItemsOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {openItemsMenuItems.map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={location.pathname === item.path || (item.path !== '/app/open-items' && location.pathname.startsWith(item.path))}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Board — compact board finance overview */}
          {isModuleEnabled('housing_board') && (
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? t('board.title', 'Board') : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation('/app/board')}
                  selected={isBoardPageActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Groups /></ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('board.title', 'Board')} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Invoicing Module */}
          {isModuleEnabled('invoicing') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleInvoicingClick}
              onMouseEnter={handleInvoicingHover}
              onMouseLeave={handlePopoverClose}
              selected={isInvoicingActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Receipt /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.invoicing')} />}
              {!sidebarCollapsed && (invoicingOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={invoicingOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {invoicingMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Sales Module */}
          {isModuleEnabled('sales') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSalesClick}
              onMouseEnter={handleSalesHover}
              onMouseLeave={handlePopoverClose}
              selected={isSalesActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><PointOfSale /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.sales')} />}
              {!sidebarCollapsed && (salesOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={salesOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {salesMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Purchasing Module */}
          {isModuleEnabled('purchasing') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handlePurchasingClick}
              onMouseEnter={handlePurchasingHover}
              onMouseLeave={handlePopoverClose}
              selected={isPurchasingActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><ShoppingCart /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.purchasing')} />}
              {!sidebarCollapsed && (purchasingOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={purchasingOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {purchasingMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Expenses Module */}
          {isModuleEnabled('expenses') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleExpensesClick}
              onMouseEnter={handleExpensesHover}
              onMouseLeave={handlePopoverClose}
              selected={isExpensesActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><ReceiptLong /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.expenses')} />}
              {!sidebarCollapsed && expensesMenuItems.length > 0 && (expensesOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={expensesOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {expensesMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* CRM Module */}
          {isModuleEnabled('crm') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleCrmClick}
              onMouseEnter={handleCrmHover}
              onMouseLeave={handlePopoverClose}
              selected={isCrmActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><People /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.crm')} />}
              {!sidebarCollapsed && (crmOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={crmOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {crmMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Inventory Module */}
          {isModuleEnabled('inventory') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleInventoryClick}
              onMouseEnter={handleInventoryHover}
              onMouseLeave={handlePopoverClose}
              selected={isInventoryActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Inventory /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.inventory')} />}
              {!sidebarCollapsed && (inventoryOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={inventoryOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {inventoryMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Accounting Module */}
          {isModuleEnabled('accounting') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleAccountingClick}
              onMouseEnter={handleAccountingHover}
              onMouseLeave={handlePopoverClose}
              selected={isAccountingActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Payment /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.accounting')} />}
              {!sidebarCollapsed && (accountingOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={accountingOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {accountingMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Bank Module */}
          {isModuleEnabled('banking') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleBankClick}
              onMouseEnter={handleBankHover}
              onMouseLeave={handlePopoverClose}
              selected={isBankActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><AccountBalance /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.bank')} />}
              {!sidebarCollapsed && (bankOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={bankOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {bankMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Salaries Module - hidden on mobile (iframe doesn't work well) */}
          {!isMobile && isModuleEnabled('salaries') && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleSalariesClick}
                  onMouseEnter={handleSalariesHover}
                  onMouseLeave={handlePopoverClose}
                  selected={isSalariesActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><Paid /></ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('nav.salaries')} />}
                  {!sidebarCollapsed && salariesMenuItems.length > 0 && (salariesOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              <Collapse in={salariesOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {salariesMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={isActive(item.path)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Time Tracking Module */}
          {isModuleEnabled('time_tracking') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleTimeTrackingClick}
              onMouseEnter={handleTimeTrackingHover}
              onMouseLeave={handlePopoverClose}
              selected={isTimeTrackingActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><AccessTime /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.time_tracking')} />}
              {!sidebarCollapsed && (timeTrackingOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={timeTrackingOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {timeTrackingMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={location.pathname === item.path}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Projects module (project tasking + my tasks list) */}
          {isModuleEnabled('projects') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleProjectsClick}
              onMouseEnter={handleProjectsHover}
              onMouseLeave={handlePopoverClose}
              selected={isProjectsActive() || isMyTasksActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><FolderSpecial /></ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.projects')} />}
              {!sidebarCollapsed && (projectsOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={projectsOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {projectsMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={location.pathname === item.path || (item.path === '/app/projects' && isProjectsActive() && !isMyTasksActive())}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* Helpdesk - Direct link */}
          {isModuleEnabled('helpdesk') && (
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? t('nav.helpdesk') : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation('/app/helpdesk/manuals')}
                  selected={isHelpdeskActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><SupportAgent /></ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('nav.helpdesk')} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Knowledgebase - Direct link */}
          {isModuleEnabled('helpdesk') && (
            <ListItem disablePadding>
              <Tooltip title={sidebarCollapsed ? t('nav.knowledgebase', 'Tietopankki') : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation('/app/knowledgebase')}
                  selected={isKnowledgebaseActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><LibraryBooks /></ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('nav.knowledgebase', 'Tietopankki')} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Sending Service Menu with Submenu */}
          {isModuleEnabled('sending_service') && (<>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSendingServiceClick}
              onMouseEnter={handleSendingServiceHover}
              onMouseLeave={handlePopoverClose}
              selected={isSendingServiceActive()}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}>
                <Send />
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={t('nav.sending_service')} />}
              {!sidebarCollapsed && (sendingServiceOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={sendingServiceOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {sendingServiceMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive(item.path)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          </>)}

          {/* My desktop — cross-tenant landing page (/me) */}
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('nav.me', 'My desktop') : ''} placement="right">
              <ListItemButton
                onClick={() => navigate('/me')}
                selected={location.pathname === '/me' || location.pathname.startsWith('/me/')}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}>
                  <AccountCircle />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('nav.me', 'My desktop')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Settings - opens finder dialog */}
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('nav.settings') : ''} placement="right">
              <ListItemButton
                onClick={handleSettingsClick}
                selected={isSettingsActive()}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}>
                  <Settings />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('nav.settings')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Separator between module entries and the Viilu-branded
              entries (Store, Support, Admin) at the bottom. */}
          <Divider sx={{ my: 1 }} />

          {/* Store */}
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('store.title') : ''} placement="right">
              <ListItemButton
                onClick={() => openStore()}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><ViiluStoreIcon width={24} height={24} /></ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('store.title')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Viilu Support — opens as a dialog, same UX as the Store; auto-
              creates a partner row in the configured Viilu Solutions tenant
              on first use (VII-209). */}
          <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? t('nav.viilu_support', 'Viilu Support') : ''} placement="right">
              <ListItemButton
                onClick={() => openSupport()}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}><SupportAgent /></ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={t('nav.viilu_support', 'Viilu Support')} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* Admin Menu - Only visible to admin users */}
          {user?.is_admin && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleAdminClick}
                  onMouseEnter={handleAdminHover}
                  onMouseLeave={handlePopoverClose}
                  selected={isAdminActive()}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56 }}>
                    <AdminPanelSettings />
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={t('admin.admin')} />}
                  {!sidebarCollapsed && (adminOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              <Collapse in={adminOpen && !sidebarCollapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {adminMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={isActive(item.path)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>

        {/* Sidebar Toggle Button */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', mt: 'auto' }}>
          <IconButton
            onClick={handleSidebarToggle}
            sx={{
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, md: `${drawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" />}
              aria-label="breadcrumb"
              maxItems={isMobile ? 2 : undefined}
              itemsAfterCollapse={isMobile ? 1 : undefined}
              itemsBeforeCollapse={isMobile ? 0 : undefined}
              sx={{
                minWidth: 0,
                '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                '& .MuiBreadcrumbs-li': { minWidth: 0 },
                '& .MuiBreadcrumbs-li > *': { minWidth: 0, maxWidth: '100%', whiteSpace: 'nowrap' },
                '& .MuiBreadcrumbs-li span': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiBreadcrumbs-separator': {
                  mx: { xs: 0.25, sm: 0.5 },
                },
              }}
            >
              {getBreadcrumbs().map((crumb, index) => {
                const isLast = index === getBreadcrumbs().length - 1;
                const isFirst = index === 0;

                if (isLast || (!crumb.path && !crumb.onClick)) {
                  return (
                    <Typography
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.25, sm: 0.5 },
                        color: 'text.primary',
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      }}
                    >
                      {crumb.icon}
                      <Box
                        component="span"
                        sx={{
                          display: { xs: isFirst ? 'none' : 'inline', sm: 'inline' },
                        }}
                      >
                        {crumb.label}
                      </Box>
                    </Typography>
                  );
                }

                return (
                  <Link
                    key={index}
                    component="button"
                    onClick={crumb.onClick || (() => handleNavigation(crumb.path))}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 0.25, sm: 0.5 },
                      color: 'text.secondary',
                      textDecoration: 'none',
                      fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {crumb.icon}
                    <Box
                      component="span"
                      sx={{
                        display: { xs: isFirst ? 'none' : 'inline', sm: 'inline' },
                      }}
                    >
                      {crumb.label}
                    </Box>
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
          <Tooltip title="AI-avustaja">
            <IconButton color="inherit" onClick={toggleChat} sx={{ mr: 0.5 }}>
              <ChatBubbleOutline sx={{ color: isChatOpen ? 'secondary.light' : 'inherit' }} />
            </IconButton>
          </Tooltip>
          {!isMobile && (
            <Tooltip title={t('nav.settings')}>
              <IconButton color="inherit" onClick={handleSettingsClick} sx={{ mr: 1 }}>
                <Settings />
              </IconButton>
            </Tooltip>
          )}
          {currentTenant && !isMobile && (
            <Box
              onClick={handleTenantMenuOpen}
              title={currentTenant.display_name || currentTenant.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                flexShrink: 1,
                maxWidth: { sm: '40vw', lg: 'none' },
                gap: 1,
                mr: 2,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                cursor: tenants.length > 1 ? 'pointer' : 'default',
                transition: 'background-color 0.2s',
                '&:hover': tenants.length > 1 ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                } : {},
              }}
            >
              <Business sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  maxWidth: '55ch',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentTenant.display_name || currentTenant.name}
              </Typography>
              {tenants.length > 1 && <ExpandMore sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />}
            </Box>
          )}
          {currentTenant && (
            <Tooltip title={t('activity.title', { defaultValue: 'Activity' })}>
              <IconButton
                size="small"
                onClick={() => navigate('/app/activity')}
                sx={{ color: 'action.active', mr: 0.5 }}
                aria-label="activity"
              >
                <ChatBubbleOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {currentTenant && (
            <Box sx={{ mr: 1 }}>
              <NotificationBell
                tenantId={currentTenant.id}
                onNavigate={(linkUrl) => {
                  // The backend resolves the canonical click-through URL for
                  // known resource_types (project_task today). New event
                  // types add their own resolver server-side; the host just
                  // navigates wherever it's told.
                  if (linkUrl) navigate(linkUrl);
                }}
              />
            </Box>
          )}
          <IconButton
            color="inherit"
            onClick={handleUserMenuOpen}
            size="large"
          >
            <Avatar
              src={userProfile?.photo_url}
              sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
            >
              {userProfile?.firstname?.[0] || user?.email?.[0]?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 240,
              maxWidth: 320,
              mt: 1,
              borderRadius: 2,
              boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={userProfile?.photo_url}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            {userProfile?.firstname?.[0] || user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userProfile?.firstname && userProfile?.lastname
                ? `${userProfile.firstname} ${userProfile.lastname}`
                : user?.displayName || user?.email}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Mobile Tenant Selection */}
        {isMobile && currentTenant && (
          <>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                {t('nav.select_tenant', 'Tenant')}
              </Typography>
              <Box
                onClick={tenants.length > 1 ? handleTenantMenuOpen : undefined}
                title={currentTenant.display_name || currentTenant.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  cursor: tenants.length > 1 ? 'pointer' : 'default',
                }}
              >
                <Business fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {currentTenant.display_name || currentTenant.name}
                </Typography>
                {tenants.length > 1 && <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {/* Menu Items */}
        <Box sx={{ py: 1 }}>
          <MenuItem
            onClick={() => { handleUserMenuClose(); navigate('/app/company-settings'); }}
            sx={{
              px: 3,
              py: 1.5,
              gap: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Business fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('nav.company_settings')}
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={handleSettings}
            sx={{
              px: 3,
              py: 1.5,
              gap: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <AccountCircle fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('nav.user_settings')}
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => { handleUserMenuClose(); handleSettingsClick(); }}
            sx={{
              px: 3,
              py: 1.5,
              gap: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Settings fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('nav.settings')}
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => { handleUserMenuClose(); rawNavigate('/me'); }}
            sx={{
              px: 3,
              py: 1.5,
              gap: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <FactCheck fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('nav.my_desktop', 'Oma työpöytä')}
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 3,
              py: 1.5,
              gap: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Logout fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {t('nav.logout')}
            </Typography>
          </MenuItem>
        </Box>

        <Divider />

        {/* Preferences */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DarkModeToggle />
            <LanguageSwitcher />
          </Box>
        </Box>
      </Menu>

      {/* Tenant Selection Menu */}
      <Menu
        anchorEl={tenantMenuAnchor}
        open={Boolean(tenantMenuAnchor)}
        onClose={handleTenantMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              // Reserve room for a 55-character name plus icons and padding.
              fontSize: '0.875rem',
              width: 'calc(55ch + 112px)',
              maxWidth: 'calc(100vw - 32px)',
              mt: 1,
              borderRadius: 2,
              boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {t('nav.select_tenant', 'Select Tenant')}
          </Typography>
        </Box>
        {tenants.length > 5 && (
          <Box sx={{ px: 1.5, pt: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={t('common.search', 'Search...')}
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}
        <List sx={{ py: 1, maxHeight: 300, overflow: 'auto' }}>
          {tenants.filter((tenant) => !tenantSearch || (tenant.display_name || tenant.name || '').toLowerCase().includes(tenantSearch.toLowerCase())).map((tenant) => (
            <ListItem key={tenant.id} disablePadding>
              <ListItemButton
                onClick={() => handleTenantSelect(tenant)}
                title={tenant.display_name || tenant.name}
                selected={currentTenant?.id === tenant.id}
                sx={{
                  px: 2,
                  py: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Business fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={tenant.display_name || tenant.name}
                  sx={{ minWidth: 0 }}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    whiteSpace: 'normal',
                    sx: { overflowWrap: 'anywhere' },
                  }}
                />
                {currentTenant?.id === tenant.id && (
                  <Check fontSize="small" sx={{ color: 'primary.main', ml: 1, flexShrink: 0 }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthExpanded,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              zIndex: (theme) => theme.zIndex.drawer,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Popover Menu for Collapsed Sidebar */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          ml: 1,
          pointerEvents: 'none',
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverClose,
            sx: {
              minWidth: 200,
              maxWidth: 280,
              borderRadius: 2,
              boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
              pointerEvents: 'auto',
              overflow: 'visible',
            },
          },
        }}
      >
        {/* Popover Title */}
        {popoverTitle && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.primary',
              }}
            >
              {popoverTitle}
            </Typography>
          </Box>
        )}

        {/* Menu Items */}
        <List sx={{ py: 1 }}>
          {popoverContent.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handlePopoverItemClick(item.path)}
                selected={isActive(item.path)}
                sx={{
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  borderRadius: 1,
                  mx: 1,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'light' ? '#EFF6FF' : '#2C2C2C',
                    color: theme.palette.mode === 'light' ? '#2563EB' : '#60A5FA',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? '#DBEAFE' : '#3C3C3C',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive(item.path) ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>

      <SettingsFinderDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onNavigate={handleNavigation}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* AppBar spacer */}
        <Box sx={{ minHeight: 64, flexShrink: 0 }} />

        {/* Migration warning banner */}
        <MigrationWarningBanner />

        {/* Content area with optional chat panel */}
        <Box sx={{ flexGrow: 1, display: 'flex', minHeight: 0 }}>
          {/* Scrollable content area */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                ...(location.pathname.includes('/board')
                  ? { px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3 }, boxSizing: 'border-box' }
                  : {}),
              }}
            >
              <Outlet />
            </Box>
          </Box>
          {/* Chat panel */}
          <ChatPanel />
        </Box>
      </Box>
    </Box>
  );
}
